import { Fab, Grid, makeStyles } from "@material-ui/core";
import { Add, Clear } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { authProvider } from "../../../../../authProvider";
import { useAuth } from "../../../../../context/auth";
import { actionShowSpinner } from "../../../../../redux-flow/action/Wrapper/Spinner";
import { getMembersByName } from "../../../../../services/formCombos.service.js";
import {
  addGeneralComment,
  addGeneralCommentRevision,
  getById,
} from "../../../../../services/minutes.service";
import { Select } from "../../../../Common/Select";
import Toastmessage from "../../../../Common/ToastMessage";
import { checkAccess } from "../../../utils/index";
import ModalBase from "../../../utils/ModalBase";
import ReviewsModal from "./ReviewsModal";
import { Label } from "./styles";

import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
  },
  subtitle: {
    ...theme.typography.subtitle1,
  },
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  spacer: {
    marginBottom: 20,
  },
  title: {
    fontWeight: "bold",
    fontSize: "1.25rem",
    color: theme.palette.primary.main,
    marginBottom: 20,
  },
}));

/**
 * @typedef {Object} GeneralComments
 * @property {string} description_GeneralComments - The description of the general comment.
 * @property {Array} generalCommentsHistoric - The history of the general comments.
 */

/**
 * @typedef {Object} GeneralCommentProps
 * @property {string} [minuteStatus] - The status of the minute.
 * @property {string} [minuteSlotId] - The ID of the minute slot.
 * @property {() => void} setGeneralComment - Function to set the general comment.
 * @property {GeneralComments} [generalComment] - The general comment object.
 * @property {boolean} isEditMode - Whether the form is in edit mode.
 * @property {import("i18next").TFunction} t - The translation function.
 */

/**
 * GeneralComment component.
 * @param {GeneralCommentProps} props - The props for GeneralComment.
 */
const GeneralComment = ({
  t,
  generalComment,
  setGeneralComment,
  minuteSlotId,
  minuteStatus,
  isEditMode = true,
}) => {
  const classes = useStyles();
  const { userAccess } = useAuth();

  const [message, setMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [generalCommentInputValue, setGeneralCommentInputValue] = useState(
    generalComment?.description_GeneralComments ?? ""
  );
  const [generalCommentReviews, setGeneralCommentReviews] = useState([]);

  const [inputReviewerRequester, setInputReviewerRequester] = useState("");
  const [reviewerRequester, setReviewerRequester] = useState();
  const [openReviewsModal, setOpenReviewsModal] = useState(false);
  const [reviewRequesters, setReviewRequesters] = useState([]);

  const [canCreateGeneralComment, setCanCreateGeneralComment] = useState(false);
  const [canCreateGeneralCommentReview, setCanCreateGeneralCommentReview] =
    useState(false);

  const [formMode, setFormMode] = useState("create");

  useEffect(() => {
    async function load() {
      if (inputReviewerRequester?.length >= 3) {
        const res = await getMembersByName("pt", inputReviewerRequester);

        if (res.error?.length > 0) {
          setReviewRequesters([]);
        }

        const list = res.data;

        setReviewRequesters(list);
      } else {
        setReviewRequesters([]);
      }
    }

    if (userAccess) {
      const hasAccessToCreateGeneralComment = checkAccess(
        userAccess,
        "createGeneralComment"
      );
      const hasAccessToCreateGeneralCommentReview = checkAccess(
        userAccess,
        "deleteGeneralComment"
      );

      setCanCreateGeneralComment(
        hasAccessToCreateGeneralComment && minuteStatus !== "C"
      );
      setCanCreateGeneralCommentReview(
        hasAccessToCreateGeneralCommentReview && minuteStatus !== "C"
      );

      load();
    }
  }, [inputReviewerRequester, minuteStatus]);

  useEffect(() => {
    setGeneralCommentReviews(generalComment?.generalCommentsHistoric || []);
    setGeneralCommentInputValue(
      generalComment?.description_GeneralComments || ""
    );

    if (
      !generalComment?.description_GeneralComments &&
      typeof generalComment?.description_GeneralComments !== "string"
    ) {
      setFormMode("create");
    } else {
      setFormMode("viewer");
    }
  }, [generalComment]);

  const loadGeneralCommentsReviews = () => {
    getById(minuteSlotId).then((res) => {
      setGeneralComment(res.generalComments);
    });
  };

  const handleAddComment = async () => {
    if (!generalCommentInputValue) {
      setMessage({
        open: true,
        content: t(
          "forms.minutes.actions.addGeneralCommentsCommentFieldValidation"
        ),
        type: "error",
        autoHideDuration: 4000,
      });

      return;
    }

    const response = await addGeneralComment({
      minuteSlotId,
      generalComments: generalCommentInputValue,
    });

    if (response?.error) {
      setMessage({
        open: true,
        content: t("forms.minutes.actions.addGeneralCommentsError"),
        type: "error",
        autoHideDuration: 4000,
      });

      return;
    }

    setMessage({
      open: true,
      content: t("forms.minutes.actions.addGeneralCommentsSuccess"),
      type: "success",
      autoHideDuration: 4000,
    });

    loadGeneralCommentsReviews();

    setFormMode("viewer");
  };

  const handleAddReview = async () => {
    if (!reviewerRequester.id) {
      setMessage({
        open: true,
        content: "Por favor preencha o campo de solicitante da revisão.",
        type: "error",
        autoHideDuration: 4000,
      });

      return;
    }

    const response = await addGeneralCommentRevision({
      generalCommentsId: generalComment?.iD_GeneralComments,
      generalComments: generalCommentInputValue,
      memberReviewer: authProvider.account.accountIdentifier,
      memberRequesterReviewer: reviewerRequester.id,
    });

    if (response?.error) {
      setMessage({
        open: true,
        content: "Ocorreu um erro ao adicionar a revisão.",
        type: "error",
        autoHideDuration: 4000,
      });

      return;
    }

    setMessage({
      open: true,
      content: "Revisão adicionada com sucesso.",
      type: "success",
      autoHideDuration: 4000,
    });

    loadGeneralCommentsReviews();
    setReviewerRequester();
    setFormMode("viewer");
  };

  return (
    <>
      <Toastmessage
        message={message.content}
        type={message.type}
        open={message.open}
        autoHideDuration={message.autoHideDuration}
        close={() =>
          setMessage({
            open: false,
            content: "",
            type: "",
            autoHideDuration: 4000,
          })
        }
      />
      <ModalBase
        open={openReviewsModal}
        setOpen={setOpenReviewsModal}
        content={
          <ReviewsModal
            t={t}
            generalComment={generalComment}
            setModalStatus={(value) => setOpenReviewsModal(value)}
          />
        }
      />
      <Grid container direction="row" spacing={3}>
        <Grid item lg={generalCommentReviews?.length > 0 ? 9 : 12} xs={12}>
          {isEditMode && (
            <Label
              errored={generalComment?.length < 1}
              disabled={formMode === "viewer" || !canCreateGeneralComment}
            >
              {t("forms.minutes.actions.generalComments")}
            </Label>
          )}

          <CKEditor
            editor={ClassicEditor}
            config={{
              toolbar: !isEditMode
                ? []
                : [
                    "heading",
                    "|",
                    "bold",
                    "italic",
                    "bulletedList",
                    "numberedList",
                  ],
            }}
            disabled={
              formMode === "viewer" || !canCreateGeneralComment || !isEditMode
            }
            data={generalCommentInputValue}
            onChange={(event, editor) => {
              setGeneralCommentInputValue(
                editor.getData().replaceAll("<br>", "<br/>")
              );
            }}
            onBlur={(event, editor) => {
              setGeneralCommentInputValue(editor.getData());
            }}
          />

          {formMode === "review" && isEditMode && (
            <Grid container spacing={3}>
              <Grid item lg={6} xs={12} style={{ marginTop: "16px" }}>
                <Select
                  value={reviewerRequester}
                  setValue={(value) => {
                    setReviewerRequester(value);
                  }}
                  id="reviewerRequester"
                  onInputChange={(_event, newInputValue, _prop) => {
                    setInputReviewerRequester(newInputValue);
                  }}
                  inputValue={inputReviewerRequester}
                  options={reviewRequesters}
                  required={true}
                  label={t("forms.minutes.actions.reviewerRequester")}
                  error={!reviewerRequester}
                  disabled={!canCreateGeneralCommentReview}
                />
              </Grid>
            </Grid>
          )}

          {formMode === "create" && isEditMode && (
            <div
              style={{
                display: "flex",
                marginTop: "62px",
                width: "100%",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Fab
                color="primary"
                aria-label="add"
                size="small"
                style={{ marginRight: "10px" }}
                onClick={handleAddComment}
                disabled={!canCreateGeneralComment}
              >
                <Add />
              </Fab>
              <span style={{ color: "#465eb6", fontWeight: "bold" }}>
                {t("forms.minutes.actions.addGeneralComments")}
              </span>
            </div>
          )}
          {formMode === "viewer" && isEditMode && (
            <div
              style={{
                display: "flex",
                marginTop: "62px",
                width: "100%",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Fab
                color="primary"
                aria-label="add"
                size="small"
                style={{ marginRight: "10px" }}
                onClick={() => setFormMode("review")}
                disabled={!canCreateGeneralCommentReview}
              >
                <Add />
              </Fab>
              <span style={{ color: "#465eb6", fontWeight: "bold" }}>
                {t("forms.minutes.actions.addReview")}
              </span>
            </div>
          )}
          {formMode === "review" && isEditMode && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "62px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  maxWidth: "200px",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  style={{ marginRight: "10px" }}
                  onClick={() => setFormMode("viewer")}
                >
                  <Clear />
                </Fab>
                <span style={{ color: "#465eb6", fontWeight: "bold" }}>
                  {t("forms.minutes.actions.cancelReview")}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  maxWidth: "200px",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  style={{ marginRight: "10px" }}
                  onClick={handleAddReview}
                  disabled={!canCreateGeneralCommentReview}
                >
                  <Add />
                </Fab>
                <span style={{ color: "#465eb6", fontWeight: "bold" }}>
                  {t("forms.minutes.actions.addReview")}
                </span>
              </div>
            </div>
          )}
        </Grid>

        <Grid item lg={3}>
          {generalCommentReviews?.length > 0 && isEditMode && (
            <div
              style={{
                border: "2px solid #bdbdbd",
                marginBottom: "8px",
                borderRadius: "8px",
                padding: "10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: "16px", overflowWrap: "break-word" }}>
                {t("forms.minutes.actions.generalCommentsReviewsTextP1")}
                <strong>{` ${generalCommentReviews?.length} `}</strong>
                {t("forms.minutes.actions.generalCommentsReviewsTextP2")}
              </span>
              <span style={{ fontSize: "16px", overflowWrap: "break-word" }}>
                <strong
                  style={{ color: "#031795", cursor: "pointer" }}
                  onClick={() => setOpenReviewsModal(true)}
                >
                  {` ${t(
                    "forms.minutes.actions.generalCommentsReviewsTextP3"
                  )} `}
                </strong>
                {t("forms.minutes.actions.generalCommentsReviewsTextP4")}
              </span>
            </div>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const withRouterGeneralComment = withRouter(GeneralComment);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

// @ts-ignore
const GeneralCommentConnect = connect(
  null,
  mapDispatchToProps
)(withRouterGeneralComment);

export default withTranslation()(GeneralCommentConnect);
